import { ClipboardModule } from '@angular/cdk/clipboard';
import { JsonPipe } from '@angular/common';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { form, FormField, maxLength, minLength, required, SchemaPathTree } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { of } from 'rxjs';
import { CableEditComponent } from '../../cables/cables-edit/cable-edit.component';
import { CableEditService } from '../../cables/cables-edit/cable-edit.service';
import { CableCommentsDto, CableDto, CableSummaryDto, CableUpdateRequestDto, MceApiService, ProjectCommentsDto, ProjectDto, ProjectUpdateRequestDto } from '../../core/mceapi.client.generated';
import { appOpt, getNoneOptionDto } from '../../core/mceapi.client.helpers';
import { ProjectEditComponent } from '../../projects/project-edit/project-edit.component';
import { ProjectEditService } from '../../projects/project-edit/project-edit.service';
import { ProjectUsersComponent } from '../../projects/project-users/project-users.component';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { isNone, isSome, none, Option } from '../../shared/functions/functional';

export interface Support {
  searchString: string;
}

export function buildSupportSchema(path: SchemaPathTree<Support>) {
  required(path.searchString, { message: `Search String is required` });
  minLength(path.searchString, 32, { message: `Search String must be at least 32characters` });
  maxLength(path.searchString, 62, { message: `Search String must be at most 32 characters` });
}

enum DisplayMode {
  DEFAULT = 'DEFAULT',
  EDITPROJECT = 'EDITPROJECT',
  EEDITPROJECTUSERS = 'EDITPROJECTUSERS',
  EDITCABLE = 'EDITCABLE'
} 


@Component({
    selector: 'app-support',
  imports: [FormField, MatInputModule, MatButtonModule, InfoMessageComponent,
    MatIconModule, MatTooltipModule, ClipboardModule,
    ProjectEditComponent, ProjectUsersComponent, CableEditComponent,
    JsonPipe, MatIconModule, MatProgressSpinnerModule, RouterLink],
    templateUrl: './support.component.html'
})
export class SupportComponent {

  readonly supportModel = signal<Support>({
    searchString: ''
  });

  readonly form = form<Support>(this.supportModel, buildSupportSchema);

  displayMode = signal<DisplayMode>(DisplayMode.DEFAULT);
  editProject = signal<ProjectDto>({});
  editProjectComments = signal<Option<ProjectCommentsDto>>(none);
  editCable = signal<CableDto>({});
  editCableComments = signal<Option<CableCommentsDto>>(none);
  processing = signal(false);

  private projectEditService = inject(ProjectEditService);
  private cableEditService = inject(CableEditService);
  private mceApiService = inject(MceApiService);

  uidSearchRx = rxResource({
    stream: () => {
      return (this.form.searchString().value() === '')
        ? of(undefined)
        : this.mceApiService.v1Support(this.form.searchString().value());
    }
  });
  uidSearch = computed(() =>
    this.uidSearchRx?.value()
    ?? { case: 'initial', project: getNoneOptionDto<ProjectDto>(), cable: getNoneOptionDto<CableDto>() });
  project = computed<Option<ProjectDto>>(() => appOpt<ProjectDto>(this.uidSearch().project ?? getNoneOptionDto<ProjectDto>()));
  cable = computed<Option<CableDto>>(() => appOpt<CableDto>(this.uidSearch().cable ?? getNoneOptionDto<CableDto>()));


  projectCablesRx = rxResource({
    params: () => {
      const project = this.project();
      return { projectUid: isSome(project) ? project.value.uid : undefined };
    },
    stream: ({ params }) =>
      params.projectUid ? this.mceApiService.v1CablesProjectSummary(params.projectUid) : of([])
  });
  projectCables = computed<CableSummaryDto[]>(() => this.projectCablesRx.value() ?? []);


  viewState = computed(() => {
    const project = this.project();
    const cable = this.cable();

    return {

    noResult: isNone(project) && isNone(cable) && this.uidSearch().case !== 'initial',

    cableResult: isSome(cable),
    projectResult: isSome(project),

    project: isSome(project) ? project.value : undefined,
    cable: isSome(cable) ? cable.value : undefined,

    showProjectEditForm: this.displayMode() === DisplayMode.EDITPROJECT,
    showProjectUsersForm: this.displayMode() === DisplayMode.EEDITPROJECTUSERS,
    showCableEditForm: this.displayMode() === DisplayMode.EDITCABLE

  };
  })

  search() {  this.uidSearchRx.reload(); }

  async onEditProject() {
    const project = this.viewState().project;
    if (!project?.uid) return;

    try {
      this.processing.set(true);
      let comments = await this.projectEditService.getProjectComments(project.uid);
      this.editProject.set(project);
      this.editProjectComments.set(comments);
      this.displayMode.set(DisplayMode.EDITPROJECT);
    } finally { this.processing.set(false); }
  }

  async onEditProjectUsers() {
    const project = this.viewState().project;
    if (!project?.uid) return;

    try {
      this.editProject.set(project);
      this.displayMode.set(DisplayMode.EEDITPROJECTUSERS);
    }
    finally { this.processing.set(false); }
 }
 

  async onEditCable() {
    const cable = this.viewState().cable;
    if (!cable?.uid) return;

    try {
      this.processing.set(true);
      let comments = await this.cableEditService.getCableComments(cable.uid);
      this.editCable.set(cable);
      this.editCableComments.set(comments);
      this.displayMode.set(DisplayMode.EDITCABLE);
    } finally { this.processing.set(false); }
  }

  onCancelEdit() { this.displayMode.set(DisplayMode.DEFAULT); }

  async onEditProjectSave(request: ProjectUpdateRequestDto) {
    try {
      this.processing.set(true);
      await this.projectEditService.updateProject(request);
      this.uidSearchRx.reload();
      this.displayMode.set(DisplayMode.DEFAULT);
    } finally { this.processing.set(false); }
  }

  async onEditCableSave(request: CableUpdateRequestDto) {
    try {
      this.processing.set(true);
      await this.cableEditService.updateCable(request);
      this.uidSearchRx.reload();
      this.displayMode.set(DisplayMode.DEFAULT);
    } finally { this.processing.set(false); }
  }

}
